// Build mode: an owner reshapes their workspace in plain words, beside the screens it changes. Customers (Marine, every
// client pack, EduTask) get it in their own words; the VERANeural team gets VERA Studio's own Build for VERANeural HQ.
// VERA proposes (record types, fields, choices, names, who sees what), the server checks the plan, and nothing changes
// until the owner presses Apply. Every applied change is in the Activity log and can be undone here. No code anywhere.
// The owner can attach a paper form, a checklist or a photo (+, paste or drop) and dictate instead of typing.
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { CRM_CHANGED, crm, type BuildPlan, type CrmSchema } from "../lib/crm";
import { pastedFiles, readFiles } from "../lib/attachments";
import { dictate } from "../lib/speech";
import { vera, type Attachment } from "../api/vera";
import { Icon, Orb, useStudio } from "./ui";
import { currentLang, T } from "../lib/i18n";

type Item =
  | { kind: "user"; text: string; files?: { name: string; thumb?: string }[] }
  | { kind: "vera"; text: string; plan?: BuildPlan; state?: "open" | "applying" | "applied" | "declined" | "undone"; undoIds?: string[]; added?: number; opened?: { id: string; plural: string }[]; error?: string };

/** What an owner might ask first, per company (Marine, or a client pack by its key). */
const EXAMPLES: Record<string, string[]> = {
  marine: ["Add hull material to boats", "Add an Inspections list linked to boats", "Let crew techs add materials"],
  aecch: ["Füge den Gebäudeteil zu Mängeln hinzu", "Füge eine Liste Bewilligungen hinzu, verknüpft mit Projekten", "Füge das Feld Prüfingenieur zu Berechnungen hinzu"],
  aecus: ["Add the building area to defects", "Add a Permits list linked to projects", "Add the checking engineer to calculations"],
  dama: ["Add a key list linked to sites", "Add a checkpoint list for door patrols, linked to sites", "Let guards add incidents with photos"],
  edutask: ["Add a reporting deadline to projects", "Add a Partners list linked to applications", "Add the evaluation score to applications"],
  "edutask-sq": ["Shto afatin e raportimit te Projektet", "Shto një listë Partnerët të lidhur me Aplikimet", "Shto pikët e vlerësimit te Aplikimet"],
  hq: ["Add a store link to releases", "Add an Investor meetings list, each with an owner on the team", "Add a client health score to the sales pipeline", "Make a launch checklist for the Zurich launch"],
};

/** What Build does, at a glance, for the team. */
const HQ_CAN: { icon: string; title: string; text: string }[] = [
  { icon: "grid", title: "New lists", text: "Investors, launches, partners: anything the team tracks." },
  { icon: "sparkle", title: "Fields and choices", text: "A date, a link to a person, a status with your own steps." },
  { icon: "doc", title: "From a document", text: "Attach a sheet or a photo; VERA builds the list and fills it." },
  { icon: "history", title: "Always undoable", text: "You see every change first, and Activity takes it back." },
];
const de = () => currentLang() === "de";
const sq = () => currentLang() === "sq";
const ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rtf,.odt,.txt,.csv,.tsv,.md,.json";
/** What VERA is asked when she reads an image or document for Build mode. */
const READ_IT = "Read this attachment and write out everything in it that a workspace could track: every heading, list, column, field, checkbox and choice, with their exact names and any example values. A plain list only, no commentary.";

/** The record types a plan touched, as they are after it was applied: what the owner may want to open. */
function touched(plan: BuildPlan, after: CrmSchema): { id: string; plural: string }[] {
  const ids = new Set<string>();
  for (const { op } of plan.steps) {
    if (op.op === "add_type") { const t = after.types.find((x) => x.plural === op.plural || x.name === op.name); if (t) ids.add(t.id); }
    else if (op.type) ids.add(op.type);
  }
  for (const r of plan.rows ?? []) ids.add(r.type);
  return after.types.filter((t) => ids.has(t.id)).map((t) => ({ id: t.id, plural: t.plural }));
}

/** `start`: a request approved in Help, for Build to plan at once (n changes each time). */
export function BuildPanel({ onClose, start }: { onClose: () => void; start?: { text: string; n: number } | null }) {
  const { getToken, toast, go, consent, openConsent, profile } = useStudio();
  // The team's own workspace (VERANeural HQ) is everyone without a customer profile.
  const hq = !profile.external;
  const examples = EXAMPLES[hq ? "hq" : profile.key === "client-edutask" ? (sq() ? "edutask-sq" : "edutask") : profile.pack ?? "marine"] ?? [];
  const [owner, setOwner] = useState<{ userId: string } | null | "tech">(null);
  const [items, setItems] = useState<Item[]>([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState("");
  const [dictating, setDictating] = useState(false);
  const [help, setHelp] = useState(false);
  const stopDictation = useRef<(() => void) | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  const fileRef = useRef<HTMLInputElement>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    crm.home(getToken).then((h) => setOwner(h.me.role === "owner" ? { userId: h.me.userId } : "tech")).catch(() => setOwner("tech"));
  }, [getToken]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [items, busy]);
  useEffect(() => () => stopDictation.current?.(), []);
  // A workspace change approved in Help: plan it as soon as Build knows this person is an owner.
  const started = useRef(0);
  useEffect(() => {
    if (!start || started.current === start.n || owner === null) return;
    started.current = start.n;
    setHelp(false);
    if (owner !== "tech") void ask(start.text);
  }, [start, owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (i: number, p: Partial<Extract<Item, { kind: "vera" }>>) => setItems((xs) => xs.map((x, j) => (j === i && x.kind === "vera" ? { ...x, ...p } : x)));
  const isOwner = owner !== null && owner !== "tech";

  async function addFiles(list: FileList | File[]) {
    if (!list.length) return;
    const r = await readFiles(list, files.length);
    if (r.files.length) setFiles((f) => [...f, ...r.files]);
    if (r.skipped.length) toast(`${T("Not added:")} ${r.skipped.join("; ")}`);
  }

  async function toggleDictation() {
    if (dictating) { stopDictation.current?.(); stopDictation.current = null; setDictating(false); return; }
    if (!consent.mic) { toast(T("Turn on the microphone in Privacy first.")); openConsent(); return; }
    try {
      setDictating(true);
      stopDictation.current = await dictate(getToken,
        (said) => { const t = textRef.current; const next = t.trim() ? `${t.trimEnd()} ${said}` : said; textRef.current = next; setText(next); },
        (msg) => { toast(msg); setDictating(false); stopDictation.current = null; });
    } catch (e) { setDictating(false); toast(e instanceof Error ? e.message : T("Dictation couldn't start.")); }
  }

  /** Text files as they are; images and documents read by VERA first (vision and Document Intelligence). */
  async function readAttached(list: Attachment[]): Promise<{ name: string; text: string }[]> {
    const out: { name: string; text: string }[] = [];
    for (const f of list) {
      if (f.kind === "text" || !f.kind) { out.push({ name: f.name, text: f.text }); continue; }
      setBusy(de() ? `VERA liest ${f.name}…` : sq() ? `VERA po lexon ${f.name}…` : `Reading ${f.name}…`);
      try {
        const r = await vera.ask({ text: READ_IT, scope: profile.scopes[0] ?? "marine", attachments: [f] }, getToken);
        out.push({ name: f.name, text: r.text });
      } catch { toast(de() ? `VERA konnte ${f.name} nicht lesen. Der Rest ist angekommen.` : sq() ? `VERA nuk e lexoi dot ${f.name}. Pjesa tjetër arriti.` : `VERA couldn't read ${f.name}. The rest went through.`); }
    }
    return out;
  }

  async function ask(request: string, attached: Attachment[] = []) {
    const history = items.map((x) => ({ role: x.kind === "user" ? ("user" as const) : ("vera" as const), text: x.text }));
    setItems((xs) => [...xs, { kind: "user", text: request, files: attached.length ? attached.map((f) => ({ name: f.name, thumb: f.thumb })) : undefined }]);
    setBusy(T("VERA is planning the change…"));
    try {
      const read = attached.length ? await readAttached(attached) : [];
      setBusy(T("VERA is planning the change…"));
      const plan = await crm.plan(getToken, request, history, read);
      setItems((xs) => [...xs, { kind: "vera", text: plan.reply, plan: plan.steps.length ? plan : undefined, state: plan.steps.length ? "open" : undefined }]);
    } catch (e) {
      setItems((xs) => [...xs, { kind: "vera", text: e instanceof Error ? e.message : T("VERA didn't answer. Please try again.") }]);
    } finally { setBusy(""); }
  }

  async function apply(i: number, plan: BuildPlan) {
    patch(i, { state: "applying", error: undefined });
    try {
      const done = await crm.buildApply(getToken, plan);
      // What to undo, items first: this import, and the newest setup change by this person.
      const me = isOwner ? (owner as { userId: string }).userId : "";
      const log = await crm.changes(getToken).catch(() => []);
      const items = done.importId ? log.find((c) => c.action === "import" && c.recordId === done.importId) : undefined;
      const setup = plan.steps.length ? log.find((c) => c.action === "schema" && c.userId === me && !c.undoneBy) : undefined;
      patch(i, { state: "applied", added: done.added, undoIds: [items?.id, setup?.id].filter((x): x is string => Boolean(x)), opened: touched(plan, done.schema),
        error: done.problems.length ? `${T("Not everything went in:")} ${done.problems.slice(0, 3).join(" ")}` : undefined });
      window.dispatchEvent(new Event(CRM_CHANGED));
    } catch (e) {
      patch(i, { state: "open", error: e instanceof Error ? e.message : T("That didn't apply. Nothing changed.") });
    }
  }

  async function undo(i: number, ids: string[]) {
    try {
      for (const id of ids) await crm.undo(getToken, id);
      patch(i, { state: "undone" });
      window.dispatchEvent(new Event(CRM_CHANGED));
      toast(T("Undone. The workspace is back as it was."));
    } catch (e) { patch(i, { error: e instanceof Error ? e.message : T("Couldn't undo it.") }); }
  }

  const submit = () => {
    const t = text.trim();
    if ((!t && !files.length) || busy || !isOwner) return;
    if (dictating) { stopDictation.current?.(); stopDictation.current = null; setDictating(false); }
    const attached = files;
    setText(""); setFiles([]);
    void ask(t || T("Set up my workspace for what's attached."), attached);
  };

  return (
    <aside className={`codepanel buildpanel${hq ? " hq" : ""}`} aria-label={T("Build mode")}
      onDragOver={(e: DragEvent) => { if (isOwner) e.preventDefault(); }}
      onDrop={(e: DragEvent) => { if (!isOwner) return; e.preventDefault(); void addFiles(e.dataTransfer.files); }}>
      <div className="cp-head">
        {hq ? <Orb size={22} /> : <Icon name="sparkle" size={17} />}<b>{T("VERA Build")}</b>
        <span className="cp-team">{hq ? T("VERANeural HQ") : T("Build mode")}</span>
        <button className="iconbtn ghost" onClick={onClose} aria-label={T("Close Build mode")} title={T("Close")}><Icon name="x" size={16} /></button>
      </div>

      <div className="cp-conv">
        {!help && owner === "tech" && <div className="notice calm"><span>{T("Build mode is for the workspace's owners. Ask an owner if something should be set up differently.")}</span></div>}
        {!help && isOwner && items.length === 0 && hq && (
          <div className="cp-empty hq-intro">
            <h3>{T("Shape VERANeural HQ in plain words")}</h3>
            <p className="mute">{T("Say what the team needs and VERA builds it: lists, fields, choices and items. She shows you each change before it happens.")}</p>
            <div className="hq-can">
              {HQ_CAN.map((c) => <div key={c.title}><Icon name={c.icon} size={15} /><b>{T(c.title)}</b><span>{T(c.text)}</span></div>)}
            </div>
            <div className="hq-try">
              {examples.map((x) => <button key={x} className="hq-ex" onClick={() => void ask(T(x))}><Icon name="sparkle" size={14} /><span>{T(x)}</span></button>)}
            </div>
          </div>
        )}
        {!help && isOwner && items.length === 0 && !hq && (
          <div className="cp-empty">
            <p className="mute">{T("Tell VERA how your workspace should work: new lists, fields, choices, names, or who sees what. Attach a paper form or a photo and she'll set it up from that. You'll see the change before it happens, and you can undo it.")}</p>
            <div className="stack" style={{ gap: 6, width: "100%" }}>
              {examples.map((x) => <button key={x} className="btn" style={{ justifyContent: "flex-start" }} onClick={() => void ask(T(x))}>{T(x)}</button>)}
            </div>
          </div>
        )}
        {!help && items.map((it, i) => it.kind === "user"
          ? (
            <div key={i} className="cp-user">
              {it.files && <div className="cp-files">{it.files.map((f, j) => f.thumb ? <img key={j} src={f.thumb} alt={f.name} className="cp-thumb" /> : <span key={j} className="cp-chip"><Icon name="doc" size={13} />{f.name}</span>)}</div>}
              {it.text}
            </div>)
          : (
            <div key={i} className="stack" style={{ gap: 8 }}>
              <div className="cp-vera">{it.text}</div>
              {it.plan && (
                <div className={`cp-card ${it.state === "open" || it.state === "applying" ? "cp-approve" : ""}`}>
                  <b>{T(it.state === "applied" ? "Changed" : it.state === "undone" ? "Undone" : it.state === "declined" ? "Not changed" : "VERA will change")}</b>
                  <ul className="cp-steps">{it.plan.steps.map((s, j) => <li key={j} className={it.state === "undone" || it.state === "declined" ? "faint" : ""}>{s.say}</li>)}</ul>
                  {!!it.plan.rows?.length && (
                    <div className={it.state === "undone" || it.state === "declined" ? "faint" : ""}>
                      <span>{de()
                        ? (it.state === "applied" ? `${it.added ?? it.plan.rows.length} Einträge hinzugefügt:` : `Fügt ${it.plan.rows.length} Einträge hinzu:`)
                        : sq() ? (it.state === "applied" ? `U shtuan ${it.added ?? it.plan.rows.length}:` : `Shton ${it.plan.rows.length}:`)
                        : it.state === "applied" ? `Added ${it.added ?? it.plan.rows.length} item${(it.added ?? it.plan.rows.length) === 1 ? "" : "s"}:` : `Adds ${it.plan.rows.length} item${it.plan.rows.length === 1 ? "" : "s"}:`}</span>
                      <ul className="cp-steps cp-rows">{it.plan.rows.slice(0, 12).map((r, j) => <li key={j}>{String(r.values.name ?? Object.values(r.values)[0] ?? "")}</li>)}</ul>
                      {it.plan.rows.length > 12 && <span className="faint small">{de() ? `und ${it.plan.rows.length - 12} weitere` : sq() ? `dhe ${it.plan.rows.length - 12} të tjera` : `and ${it.plan.rows.length - 12} more`}</span>}
                    </div>
                  )}
                  {(it.state === "open" || it.state === "applying") && (
                    <div className="row" style={{ gap: 6 }}>
                      <button className="btn primary" disabled={it.state === "applying"} onClick={() => void apply(i, it.plan!)}>{it.state === "applying" ? T("Applying…") : T("Apply")}</button>
                      <button className="btn" disabled={it.state === "applying"} onClick={() => patch(i, { state: "declined" })}>{T("Not now")}</button>
                    </div>
                  )}
                  {it.state === "applied" && (
                    <>
                      <span className="faint">{T(it.added ? "The list and its items are in your workspace now, and in Activity." : "It's in your workspace now, and in Activity.")}</span>
                      <div className="row" style={{ gap: 6 }}>
                        {(it.opened ?? []).map((t) => <button key={t.id} className="btn primary" onClick={() => go("records", { crmType: t.id })}>{de() ? `${t.plural} öffnen` : sq() ? `Hap ${t.plural}` : `Open ${t.plural}`}</button>)}
                        {!!it.undoIds?.length && <button className="btn" onClick={() => void undo(i, it.undoIds!)}>{T("Undo")}</button>}
                      </div>
                    </>
                  )}
                  {it.error && <span className="cp-line warn" role="alert">{it.error}</span>}
                </div>
              )}
            </div>
          ))}
        {busy && <div className="cp-line faint">{busy}</div>}
        <div ref={end} />
      </div>

      {!help && <div className="cp-composebox">
        {files.length > 0 && (
          <div className="cp-files">{files.map((f, j) => (
            <span key={j} className="cp-chip">
              {f.thumb ? <img src={f.thumb} alt="" className="cp-chipthumb" /> : <Icon name="doc" size={13} />}{f.name}
              <button className="iconbtn ghost" aria-label={`${T("Remove")} ${f.name}`} onClick={() => setFiles((x) => x.filter((_, k) => k !== j))}><Icon name="x" size={12} /></button>
            </span>))}
          </div>
        )}
        <div className="cp-compose cp-compose2">
          <input ref={fileRef} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ""; }} />
          <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!isOwner} placeholder={T(dictating ? "Listening… speak and it appears here" : "Ask VERA to change your workspace…")}
            onPaste={(e: ClipboardEvent<HTMLTextAreaElement>) => { const f = pastedFiles(e.clipboardData); if (f.length) { e.preventDefault(); void addFiles(f); } }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
          <button className="iconbtn" disabled={!isOwner || Boolean(busy)} onClick={() => fileRef.current?.click()} aria-label={T("Attach images, files or documents")} title={T("Attach images, files or documents")}><Icon name="plus" size={17} /></button>
          <button className={`iconbtn ${dictating ? "on" : ""}`} disabled={!isOwner} aria-pressed={dictating} onClick={() => void toggleDictation()} aria-label={T(dictating ? "Stop dictating" : "Dictate")} title={T(dictating ? "Stop dictating" : "Dictate")}><Icon name={dictating ? "micoff" : "mic"} size={17} /></button>
          <button className="btn primary cp-send" disabled={(!text.trim() && !files.length) || Boolean(busy) || !isOwner} onClick={submit}><Icon name="send" size={15} />{T("Send")}</button>
        </div>
      </div>}
    </aside>
  );
}
